import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {import('./slideshow').Slideshow} zoomSlideshow - The lightbox slideshow.
 * @property {HTMLElement} thumbnails - The thumbnails container.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'zoomSlideshow', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.refs.zoomSlideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #slideshow() {
    return this.refs.zoomSlideshow;
  }

  get #slides() {
    return this.#slideshow?.slides ?? [];
  }

  /**
   * @param {number} index
   * @returns {HTMLElement | undefined}
   */
  #getSlide(index) {
    const slide = this.#slides[index];
    return slide instanceof HTMLElement ? slide : undefined;
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, thumbnails } = this.refs;
    const targetImage = this.#getSlide(index);
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      // Slideshow scroll is disabled until [in-viewport]; the dialog is off-screen until opened.
      this.#slideshow?.setAttribute('in-viewport', '');
      requestAnimationFrame(() => {
        this.#slideshow?.select(index, event, { animate: false });
        this.#syncThumbnail(index, { behavior: 'instant' });
        this.#loadSlideHighRes(index);
      });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const { index } = event.detail;
    this.#syncThumbnail(index, { behavior: 'smooth' });
    const slide = this.#getSlide(index);
    if (slide) this.loadHighResolutionImage(slide);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;
    const activeIndex = this.#slideshow?.current ?? 0;
    const mostVisibleElement = this.#getSlide(activeIndex);

    if (!supportsViewTransitions() || isLowPowerDevice() || !mostVisibleElement) return this.closeDialog();

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const slide = this.#getSlide(index);
    if (!slide) return;

    this.loadHighResolutionImage(slide);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails?.children.length) return;

    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const animate = options.behavior !== 'instant';
    await this.#slideshow?.select(index, undefined, { animate });
  }

  /**
   * @param {number} index
   * @param {Object} options
   * @param {ScrollBehavior} options.behavior
   */
  #syncThumbnail(index, options = { behavior: 'smooth' }) {
    const { thumbnails } = this.refs;
    if (!thumbnails?.children.length) return;

    const targetThumbnail = thumbnails.children[index];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * @param {number} index
   */
  #loadSlideHighRes(index) {
    const slide = this.#getSlide(index);
    if (slide) this.loadHighResolutionImage(slide);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
